import { DecimalPipe, ViewportScroller } from '@angular/common';
import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { AdjustmentService } from 'src/app/core/services/data/adjustment.services';
import { ApnUtilitiesService } from 'src/app/core/services/data/apn-utilities.service';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { InvoiceService } from 'src/app/core/services/data/invoice.service';
import { LotService } from 'src/app/core/services/data/lot.service';
import { MessageService } from 'src/app/core/services/broadcast/message-service';
import { QueryMessageService } from 'src/app/core/services/broadcast/MessageDispatchingService';
import { AddedAdjustments } from 'src/app/shared/entities/AddedAdjustments';
import { AddInvoiceAdjustment } from 'src/app/shared/entities/AddInvoiceAdjustment';
import { AddInvoiceAdjustmentTax } from 'src/app/shared/entities/AddInvoiceAdjustmentTax';
import { AdjustmentsCompletedGB } from 'src/app/shared/entities/AdjustmentsCompletedGB';
import { AdjustmentsCompletedMI } from 'src/app/shared/entities/AdjustmentsCompletedMI';
import { AdjustmentValidation } from 'src/app/shared/entities/AdjustmentValidation';
import { AdjustTaxes } from 'src/app/shared/entities/AdjustTaxes';
import { dbErrorEntry } from 'src/app/shared/entities/dbErrorEntry';
import { InvoiceAdjustment } from 'src/app/shared/entities/InvoiceAdjustment';
import { InvoiceAdjustmentReasons } from 'src/app/shared/entities/InvoiceAdjustmentReasons';
import { InvoiceAdjustments } from 'src/app/shared/entities/InvoiceAdjustments';
import { PostMigrateAdjustment } from 'src/app/shared/entities/PostMigrateAdjustment';
import { QuickSearchCriteria } from 'src/app/shared/entities/QuickSearchCriteria';
import { QuickSearchResults } from 'src/app/shared/entities/QuickSearchResults';
import { RevenueTypes } from 'src/app/shared/entities/RevenueTypes';
import { TaxListMatchingInvoiceDate } from 'src/app/shared/entities/TaxListMatchingInvoiceDate';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { IInvoiceAdjustmentAmount, ITaxableListByLotNumberResponse } from 'src/app/shared/interfaces';
import { IgxNumberAbbreviatorDynamicModule } from 'igniteui-angular-charts';

@Component({
  selector: 'app-adjust-billing',
  templateUrl: './adjust-billing.component.html',
  styleUrls: ['./adjust-billing.component.scss'],
})
export class AdjustBillingComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  set invoiceNumber(value: string) {
    this.currentInvoiceNumber = value;
    const invoiceType = this.currentInvoiceNumber.substring(0, 2);
    this.isGB = invoiceType == 'GB';
  }

  invoiceBillingLines: InvoiceAdjustments[] = [];
  invoiceAdjustmentReasons: InvoiceAdjustmentReasons[] = [];
  matchingTaxListForInvoice: TaxListMatchingInvoiceDate[] = [];
  addedAdjustments: AddedAdjustments[] = [];
  revenueTypes: RevenueTypes[] = [];
  invoicePeriod: string;
  customerName: string;
  invoiceAmount: number;
  invoiceBalance: number;
  quickSearchCriteria: QuickSearchCriteria;
  invoiceDetailData: QuickSearchResults;
  errorMessage: string = '';
  disableAdjustmentSubmitButtonFlag: boolean = true;
  adjustmentValidationMessages: AdjustmentValidation = new AdjustmentValidation();
  adjustmentDataEntryLocked: string = 'true';
  currentGenerateBillingLineItemId: string = '';
  adjustedNetRevenue: number;
  maximumAdjustmentGrossAmount: number;
  maximumAdjustmentNetAmount: number;
  adjustmentBeingAdded: boolean = false;
  isGB: Boolean;
  completedAdjustmentsGB: AdjustmentsCompletedGB[] = [];
  completedAdjustmentsMI: AdjustmentsCompletedMI[] = [];
  subInvoiceDetail!: Subscription;
  invoiceBeingAdjustedDetails: InvoiceAdjustments = new InvoiceAdjustments();
  msgService: QueryMessageService;
  taxableListForLot: ITaxableListByLotNumberResponse[] = [];
  isLineItemTaxable: boolean = false;
  currentInvoiceNumber: string;

  //Services
  _appGeneralState = inject(AppGeneralStateService);
  #_dataService = inject(DataService);

  constructor(
    private _snackBar: MatSnackBar,
    private invoiceService: InvoiceService,
    private authService: AuthService,
    public dialog: MatDialog,
    private decimalPipe: DecimalPipe,
    private adjustmentService: AdjustmentService,
    private viewportScroller: ViewportScroller,
    private lotService: LotService,
    private apnUtilityService: ApnUtilitiesService,
    messageDispatchService: QueryMessageService,
    private messageService: MessageService,
  ) {
    this.msgService = messageDispatchService;
  }

  ngOnInit(): void {
    this.onScrollToTop();
    this.getRevenueTypesForLotNumber(this._appGeneralState.currentLotNumber);
    this.getIsTaxableListByLotNumber(this._appGeneralState.currentLotNumber);
  }

  onScrollToTop(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initInvoiceAdjustmentReason();
  }

  ngOnDestroy(): void {
    this.subInvoiceDetail.unsubscribe();
  }

  initInvoiceAdjustmentReason(): void {
    this.invoiceAdjustmentReasons = this.adjustmentService.getCurrentLotAdjustmentReasons();

    this.getInvoiceDetailBalancesForInvoiceNumber(this.currentInvoiceNumber);

    this.getTaxListThatMatchInvoiceDate(this.currentInvoiceNumber);

    if (this.isGB) {
      this.getAdjustmentsCompletedForGB(this.currentInvoiceNumber);
    } else {
      this.getAdjustmentsCompletedForMI(this.currentInvoiceNumber);
    }
  }

  getTaxListThatMatchInvoiceDate(invoiceNumber: string): void {
    this.invoiceService.getTaxListThatMatchInvoiceDate(invoiceNumber).subscribe(
      (x) => {
        this.matchingTaxListForInvoice = x;
      },
      (err: any) => {},
      () => {
        let ii: number = 0;

        for (ii = 0; ii < this.matchingTaxListForInvoice.length; ii++) {
          let description: string = this.matchingTaxListForInvoice[ii].taxName;
          // remove old from tax type description
          description = description.replace(' - OLD', '');
          this.matchingTaxListForInvoice[ii].taxName = description;
        }
      },
    );
  }

  //todo:DataFetch
  getInvoiceDetailBalancesForInvoiceNumber(invoiceNumber: string): void {
    let quickSearchResults: QuickSearchResults;

    this.subInvoiceDetail = this.invoiceService
      .getInvoiceDetailByInvoiceNumber(invoiceNumber, this._appGeneralState.currentLotNumber)
      .subscribe({
        error: (err: any) => {
          let errorLogEntry: dbErrorEntry = new dbErrorEntry();

          errorLogEntry.Location = 'Err: invoice-add-revenue.ts::getInvoiceDetailBalancesForInvoiceNumber().';
          errorLogEntry.errorMessage = JSON.stringify(err);
          this.apnUtilityService.logErrorToDB(errorLogEntry);
        },
        next: (results) => (quickSearchResults = results),
        complete: () => {
          this.invoiceDetailData = quickSearchResults;
          this.setInvoiceDetailData();
        },
      });
  }

  setInvoiceDetailData(): void {
    let ii: number = 0;

    this.currentInvoiceNumber = this.invoiceDetailData.invoiceNumber;
    this.invoiceAmount = this.invoiceDetailData.rate;
    this.invoiceBalance = this.invoiceDetailData.currentBalance;

    this.msgService.currentInvoiceBalance = this.invoiceBalance;

    if (this.invoiceDetailData.customerName !== '') this.customerName = this.invoiceDetailData.customerName;
    else this.customerName = this.invoiceDetailData.companyName;

    var months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    var d = new Date(this.invoiceDetailData.invoiceDate);
    var monthName = months[d.getMonth()];
    var year = d.getFullYear();

    this.invoicePeriod = monthName + ', ' + year;
  }

  postIUAdjustment(
    invoiceAdjustmentData: AddInvoiceAdjustment,
    invoiceAdjustmentTax: AddInvoiceAdjustmentTax[],
    isTaxable: boolean,
  ): void {
    let newAdjustmentPostingId: number;
    let adjustmentData: InvoiceAdjustment = new InvoiceAdjustment();
    let adjustTaxes: AdjustTaxes = new AdjustTaxes();
    let migration: PostMigrateAdjustment = new PostMigrateAdjustment();
    let statusResult: boolean;

    adjustTaxes.adjustmentPostingId = 0; //This is just for initialization.  The actual new adjustmentPostingId is created and set in the API data methods
    adjustTaxes.invoiceNumber = invoiceAdjustmentData.InvoiceNumber;
    migration.invoiceNumber = invoiceAdjustmentData.InvoiceNumber;

    //Assign different adjustment objects to adjustmentData object
    adjustmentData.adjustmentPosting = invoiceAdjustmentData;
    adjustmentData.adjustmentPostingTax = invoiceAdjustmentTax;
    adjustmentData.adjustTaxes = adjustTaxes;
    adjustmentData.migrationAdjustment = migration;

    this.invoiceService.postInvoiceAdjustmentAllData(adjustmentData).subscribe(
      (postingAdjustmentResultStatus) => {
        statusResult = postingAdjustmentResultStatus;
      },
      (err: any) => {
        this.adjustmentBeingAdded = false;
        let errorLogEntry: dbErrorEntry = new dbErrorEntry();

        errorLogEntry.Location = 'Err: invoice-adjustments.ts::postInvoiceAdjustmentAllData().';
        errorLogEntry.errorMessage = JSON.stringify(err);
        this.apnUtilityService.logErrorToDB(errorLogEntry);

        // addExceptionMessage only logs to the console - <app-error-messages> is not placed in any
        // template, so the snackbar is the only feedback the user actually sees.
        this._snackBar.open('An error occurred and the adjustment failed to be recorded.', '', {
          duration: 5000,
        });

        this.messageService.addExceptionMessage(
          'An error occurred while submitting this adjustment. Please review the adjustment data and resubmit. If the error continues to occur, please contact APN Support. Error Id #: ' +
            err.error,
        );
      },
      () => {
        this.onResetAddNewAdjustmentData();
        this.adjustmentBeingAdded = false;

        if (statusResult) {
          //todo - change way to update quicksearch cached results
          //this.updateQuickSearchResultsBalanceForInvoice(adjustmentData.adjustmentPosting.InvoiceNumber);

          this.onResetAddNewAdjustmentData();
        } else {
          //What should we do here
        }

        this.initInvoiceAdjustmentReason();

        // let totalAdjustment: IInvoiceAdjustmentAmount = this.this.getInvoiceDetailBalancesForInvoiceNumber(
        //   this.currentInvoiceNumber,
        // );

        //This updates the array that shows historical adjustments
        // this.addedAdjustments.push({
        //   description: this.invoiceBeingAdjustedDetails.customerName,
        //   adjustmentAmount: invoiceAdjustmentData.AdjustmentAmount
        // });

        this._snackBar.open('This adjustment have been successfully submitted.', '', { duration: 3000 });
      },
    );
  }

  isRevCodeTaxable(revCode: string): boolean {
    let isTaxable: boolean;

    isTaxable = this.taxableListForLot.find((code) => code.code == revCode).isTaxable;

    return isTaxable;
  }

  getIsTaxableListByLotNumber(lotNumber: string): void {
    this.#_dataService.getIsTaxableListForLot(lotNumber).subscribe(
      (res) => {
        this.taxableListForLot = res;
      },
      (err: any) => {
        let errorLogEntry: dbErrorEntry = new dbErrorEntry();

        errorLogEntry.Location = 'Err: invoice-adjustments.ts::getIsTaxableListByLotNumber().';
        errorLogEntry.errorMessage = JSON.stringify(err);
        this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {},
    );
  }

  getAdjustmentsCompletedForGB(invoiceNumber: string): void {
    this.invoiceService.getAdjustmentsCompleted_GB(invoiceNumber).subscribe(
      (adjustmentListResults) => {
        this.completedAdjustmentsGB = adjustmentListResults;
      },
      (err: any) => {
        let errorLogEntry: dbErrorEntry = new dbErrorEntry();

        errorLogEntry.Location = 'Err: invoice-adjustments.ts::getAdjustmentsCompletedForGB().';
        errorLogEntry.errorMessage = JSON.stringify(err);
        this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {
        this.getInvoiceBilingLinesForInvoiceNumber_GB(this.currentInvoiceNumber);
      },
    );
  }

  getAdjustmentsCompletedForMI(invoiceNumber: string): void {
    this.invoiceService.getAdjustmentsCompleted_MI(invoiceNumber).subscribe(
      (adjustmentListResults) => {
        this.completedAdjustmentsMI = adjustmentListResults;
      },
      (err: any) => {
        let errorLogEntry: dbErrorEntry = new dbErrorEntry();

        errorLogEntry.Location = 'Err: invoice-adjustments.ts::getAdjustmentsCompletedForMI().';
        errorLogEntry.errorMessage = JSON.stringify(err);
        this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {
        this.getInvoiceBillingLinesForInvoiceNumber_MI(this.currentInvoiceNumber);
      },
    );
  }

  getInvoiceBilingLinesForInvoiceNumber_GB(invoiceNumber: string) {
    this.invoiceService.getAdjustmentsForInvoiceNumber_GB(invoiceNumber).subscribe(
      (invoiceBillingLines) => {
        this.invoiceBillingLines = invoiceBillingLines;
      },
      (err: any) => {
        let errorLogEntry: dbErrorEntry = new dbErrorEntry();

        errorLogEntry.Location = 'Err: invoice-adjustments.ts::getInvoiceAdjustmentsForInvoiceNumber_GB().';
        errorLogEntry.errorMessage = JSON.stringify(err);
        this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {
        //copy generateBillingLineItemID to primaryKey
        let ii: number = 0;
        for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
          this.invoiceBillingLines[ii].gbOriginalBillingDetailId = this.invoiceBillingLines[ii].billingDetailID;
        }

        ii = 0;
        for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
          this.invoiceBillingLines[ii].primaryKey = this.invoiceBillingLines[ii].generateBillingLineItemId;

          //Add any adjustmnets to invoiceBillingLines array
          if (this.invoiceBillingLines[ii].adjustment < 0) {
            this.addedAdjustments.push({
              description: this.invoiceBillingLines[ii].customerName,
              adjustmentAmount: this.invoiceBillingLines[ii].adjustment,
            });
          }

          const adjustmentTotals: IInvoiceAdjustmentAmount = this.getTotalsForInvoiceBillingLine(
            this.invoiceBillingLines[ii].generateBillingLineItemId,
          );

          this.invoiceBillingLines[ii].calcedAdjustmentAmount = adjustmentTotals.total;
        }
      },
    );
  }

  getInvoiceBillingLinesForInvoiceNumber_MI(invoiceNumber: string) {
    this.#_dataService.getMiInvoiceBillingLines(invoiceNumber).subscribe(
      (invoiceBillingLines) => {
        this.invoiceBillingLines = invoiceBillingLines;
      },
      (err: any) => {
        let errorLogEntry: dbErrorEntry = new dbErrorEntry();

        errorLogEntry.Location = 'Err: invoice-adjustments.ts::getAdjustmentsForInvoiceNumber_MI().';
        errorLogEntry.errorMessage = JSON.stringify(err);
        this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {
        //copy billingDetailId to primaryKey, manualInvoiceId to generateBillingLineItemId
        let ii: number = 0;
        for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
          this.invoiceBillingLines[ii].primaryKey = this.invoiceBillingLines[ii].billingDetailID.toString();
          this.invoiceBillingLines[ii].generateBillingLineItemId = this.invoiceBillingLines[ii].manualInvoiceId;

          //Add any adjustments to invoiceBillingLines array
          if (this.invoiceBillingLines[ii].adjustment < 0) {
            this.addedAdjustments.push({
              description: this.invoiceBillingLines[ii].customerName,
              adjustmentAmount: this.invoiceBillingLines[ii].adjustment,
            });
          }
          const adjustmentTotals: IInvoiceAdjustmentAmount = this.getTotalsForInvoiceBillingLine(
            this.invoiceBillingLines[ii].billingDetailID,
          );

          this.invoiceBillingLines[ii].calcedAdjustmentAmount = adjustmentTotals.total;
        }
      },
    );
  }

  isInvoiceGBType(invoiceNumber: string): boolean {
    let isGB: boolean;

    if (invoiceNumber.length === 0) isGB = false;

    if (invoiceNumber.substring(0, 2) === 'GB') isGB = true;
    else isGB = false;

    return isGB;
  }

  onSubmitInvoiceAdjustment(): void {
    if (this.adjustmentBeingAdded == true) return;

    this.adjustmentBeingAdded = true;

    if (this.validateNewInvoiceAdjustment()) {
      let invoiceAdjustmentDetails: AddInvoiceAdjustment = new AddInvoiceAdjustment();
      let invoiceAdjustmentTaxDetails: AddInvoiceAdjustmentTax[] = [];
      let primaryKeyForAdjustmentLine: string = (<HTMLOptionElement>(
        document.getElementById('adjustmentInvoiceLineItem')
      )).value;
      this.invoiceBeingAdjustedDetails = this.getInvoiceAdjustmentDetailsForLineItem(primaryKeyForAdjustmentLine);

      let grossAmount: number = this.parseFloatIgnoreCommas(
        (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value,
      );

      this.adjustedNetRevenue = this.parseFloatIgnoreCommas(
        (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value,
      );

      invoiceAdjustmentDetails.AdjustmentAmount = this.adjustedNetRevenue;
      invoiceAdjustmentDetails.AdjustmentReasonId = this.getAdjustmentReasonId();
      if (this.isGB) {
        invoiceAdjustmentDetails.BillingDetailId = 0;
      } else {
        invoiceAdjustmentDetails.BillingDetailId = this.invoiceBeingAdjustedDetails.billingDetailID;
      }

      invoiceAdjustmentDetails.Comments = (<HTMLInputElement>document.getElementById('adjustmentComment')).value;
      invoiceAdjustmentDetails.GenerateBillingLineItemId = this.invoiceBeingAdjustedDetails.generateBillingLineItemId;
      invoiceAdjustmentDetails.InvoiceNumber = this.currentInvoiceNumber;
      invoiceAdjustmentDetails.IsLocked = false;
      invoiceAdjustmentDetails.LotNumber = this.invoiceDetailData.lotNumber;
      invoiceAdjustmentDetails.RevenueCode = ''; //this.getRevenueCodeFromRevenueName(this.invoiceBeingAdjustedDetails.description);
      invoiceAdjustmentDetails.UserName = this._appGeneralState.userName;
      invoiceAdjustmentDetails.ManualInvoiceId = this.invoiceBeingAdjustedDetails.manualInvoiceId;

      //todo refactor in separate method
      let ii: number = 0;
      let today = new Date();
      if (this.isLineItemTaxable) {
        invoiceAdjustmentTaxDetails.push({
          AdjustmentPostingTaxId: 0,
          AdjustmentPostingId: 0,
          BillingDetailId: 0,
          TaxType: 'Total',
          AdjustedTax: grossAmount - this.adjustedNetRevenue,
          AdjustedTaxMoney: grossAmount - this.adjustedNetRevenue,
          DateEntered: today,
        });
      }

      this.postIUAdjustment(invoiceAdjustmentDetails, invoiceAdjustmentTaxDetails, this.isLineItemTaxable);
    }
  }

  getRevenueTypesForLotNumber(lotNumber: string) {
    let tempRevenueTypes: RevenueTypes[];
    this.invoiceService.getRevenueTypesForLotNumber(lotNumber).subscribe(
      (revenueTypes) => {
        tempRevenueTypes = revenueTypes;
      },
      (err: any) => {
        let errorLogEntry: dbErrorEntry = new dbErrorEntry();

        errorLogEntry.Location = 'Err: invoice-adjustments.ts::getRevenueTypesForLotNumber().';
        errorLogEntry.errorMessage = JSON.stringify(err);
        this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {
        this.revenueTypes = tempRevenueTypes;
      },
    );
  }

  getRevenueCodeFromRevenueName(revenueDescription: string): string {
    let ii: number = 0;
    let revenueCode: string = '';
    try {
      for (ii = 0; ii < this.revenueTypes.length; ii++) {
        if (this.revenueTypes[ii].description.toLowerCase() === revenueDescription.toLowerCase()) {
          revenueCode = this.revenueTypes[ii].code;
        }
      }
    } catch (err) {
      console.error('Error on Revenue Code Match: ' + JSON.stringify(err));
    }

    return revenueCode;
  }

  getAdjustmentReasonId(): number {
    let adjustmentReasonId: number;

    var e = document.getElementById('adjustmentReasons') as HTMLSelectElement;
    var sel = e.selectedIndex;
    var opt = e.options[sel];
    adjustmentReasonId = parseInt((<HTMLOptionElement>opt).value);

    return adjustmentReasonId;
  }

  isInvoiceLineItemTaxableOnCurrentAdjustment(primaryKeyValue: string): boolean {
    let isTaxable: boolean = false;

    if (this.invoiceBillingLines.length <= 0) return false;

    let ii: number = 0;

    for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
      if (this.invoiceBillingLines[ii].primaryKey === primaryKeyValue) {
        if (this.invoiceBillingLines[ii].tax > 0) {
          isTaxable = true;
        }
      }
    }

    return isTaxable;
  }

  validateNewInvoiceAdjustment(): boolean {
    let isValid: boolean = true;

    return isValid;
  }

  onCalcAmountFromGross(): void {
    /*
    let grossValue: string = (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value;
    try {
      let ii: number = 0;
      let totalTaxRate: number = 0;
      for (ii = 0; ii < this.matchingTaxListForInvoice.length; ii++) {
        totalTaxRate = totalTaxRate + this.matchingTaxListForInvoice[ii].taxPercentage / 100;
      }

      if (grossValue === '') return;

      let grossAsNumber: number = this.parseFloatIgnoreCommas(grossValue);

      if (isNaN(grossAsNumber)) {
        return;
      }

      //Evaluate whether this is a positive number.  If > 0, then make negative
      if (grossAsNumber > 0) {
        grossAsNumber = grossAsNumber * -1;
        (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value = grossAsNumber.toString();
      }

      let generateBillingLineItemId: string;
      this.disableAdjustmentSubmitButtonFlag = true;

      var e = document.getElementById('adjustmentInvoiceLineItem') as HTMLSelectElement;
      var sel = e.selectedIndex;
      var opt = e.options[sel];
      generateBillingLineItemId = (<HTMLOptionElement>opt).value;

      //Marked for deletion
      //let isInvoiceLineTaxable: boolean = this.isInvoiceLineItemTaxable(generateBillingLineItemId);

      let netAmount: number = 0;

      if (this.isInvoiceLineItemTaxable) {
        netAmount = grossAsNumber / (1 + totalTaxRate);
      } else netAmount = grossAsNumber;

      this.maximumAdjustmentNetAmount = netAmount;

      this.adjustedNetRevenue = netAmount; //This is used to calculate actual tax amounts without losing precision.

      let netAmountDisplay: any = this.decimalPipe.transform(netAmount, '1.2-2');

      (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value = netAmountDisplay;
    } catch (err) {
      console.error('onCalcAmountFromGross Error: ' + err);
    }
      */
  }

  getNetLineAmountTotal(): number {
    let netAmountOfBillingLine: number = 0;

    var e = document.getElementById('adjustmentInvoiceLineItem') as HTMLSelectElement;
    var sel = e.selectedIndex;
    var opt = e.options[sel];
    const billingDetailId: any = (<HTMLOptionElement>opt).value;

    //Get the current billing line item id for the line the user has selected
    const currentBillingLineItem: InvoiceAdjustments = this.invoiceBillingLines.find(
      (invoice) => (invoice.billingDetailID = billingDetailId),
    );

    //Get the value of all the adjustments
    const adjustmentTotals: IInvoiceAdjustmentAmount = this.getTotalsForInvoiceBillingLine(billingDetailId);

    //Calc the net amount available
    netAmountOfBillingLine = currentBillingLineItem.totalAmount + adjustmentTotals.total;

    return netAmountOfBillingLine;
  }

  onCalcAmountFromNet(): void {
    let netRawValue: string = (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value;
    let ii: number = 0;

    try {
      let generateBillingLineItemId: string;
      this.disableAdjustmentSubmitButtonFlag = true;

      var e = document.getElementById('adjustmentInvoiceLineItem') as HTMLSelectElement;
      var sel = e.selectedIndex;
      var opt = e.options[sel];
      generateBillingLineItemId = (<HTMLOptionElement>opt).value;
      let isInvoiceLineTaxable: boolean = this.isInvoiceLineItemTaxable(generateBillingLineItemId);

      let totalTaxRate: number = 0;
      for (ii = 0; ii < this.matchingTaxListForInvoice.length; ii++) {
        totalTaxRate = totalTaxRate + this.matchingTaxListForInvoice[ii].taxPercentage / 100;
      }

      if (netRawValue === '') return;

      let netAsNumber: number = this.parseFloatIgnoreCommas(netRawValue);
      //Evaluate whether this is a positive number.  If > 0, then make negative
      if (netAsNumber > 0) {
        netAsNumber = netAsNumber * -1;
        (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value = netAsNumber.toString();
      }

      this.adjustedNetRevenue = netAsNumber; //This is used to calculate taxes during the adjustment process
      if (isNaN(netAsNumber)) {
        return;
      }

      let grossAmount: number = 0;

      if (this.isInvoiceLineItemTaxable) {
        grossAmount = totalTaxRate * netAsNumber + netAsNumber;
      } else grossAmount = netAsNumber;

      let grossAmountToDisplay: any = this.decimalPipe.transform(grossAmount, '1.2-2')?.toString();
      let grossAmountAsNumber: number = +grossAmountToDisplay;

      const maxNetAmountAsString = this.decimalPipe.transform(this.maximumAdjustmentNetAmount, '1.2-2')?.toString();
      const maxGrossAmountAsString = this.decimalPipe.transform(this.maximumAdjustmentGrossAmount, '1.2-2')?.toString();

      const maxNetAmountAsNumber = +maxNetAmountAsString;
      const maxGrossAmountAsNumber = +maxGrossAmountAsString;

      const currentNetValueOfInvoiceLine: number = this.getNetLineAmountTotal();

      if (grossAmount * -1 > currentNetValueOfInvoiceLine) {
        grossAmountToDisplay = this.decimalPipe.transform(this.invoiceBalance, '1.2-2')?.toString();
      }

      (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value = grossAmountToDisplay;
    } catch (err) {
      console.error('onCalcAmountFromNet Error: ' + err);
    }
  }

  isInvoiceLineItemTaxable(generateBillingLineItemId: string): boolean {
    let ii: number = 0;
    let isTaxable = false;

    try {
      for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
        if (this.invoiceBillingLines[ii].generateBillingLineItemId === generateBillingLineItemId) {
          isTaxable = this.invoiceBillingLines[ii].isTaxActive === true;
        }
      }
    } catch (err) {
      console.error('Error on IsInvoiceLineItemTaxable(): ' + err);
    }

    return isTaxable;
  }

  onResetAddNewAdjustmentData(): void {
    try {
      this.disableAdjustmentSubmitButtonFlag = true;

      if (this.invoiceBillingLines.length > 0)
        (<HTMLOptionElement>document.getElementById('adjustmentInvoiceLineItem')).value = '0';

      (<HTMLInputElement>document.getElementById('adjustmentInvoiceLineCurrentDate')).value = this.getCurrentDate();
      (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value = '';
      (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value = '';
      (<HTMLOptionElement>document.getElementById('adjustmentReasons')).value = '0';
      (<HTMLInputElement>document.getElementById('adjustmentComment')).value = '';

      this.adjustmentValidationMessages = new AdjustmentValidation();
    } catch (err) {}
  }

  // updateInvoiceDetailsForAdjustment(): void {
  //   var e = document.getElementById('adjustmentInvoiceLineItem') as HTMLSelectElement;
  //   var sel = e.selectedIndex;
  //   var opt = e.options[sel];
  //   const lineItemId = (<HTMLOptionElement>opt).value;

  //   const typeInvoice = this.currentInvoiceNumber.substring(0, 2);
  //   console.log('invoice number', typeInvoice);

  //   if (typeInvoice == 'MI') this.getMiRevenueCodeForLinteItem(lineItemId);

  //   if (typeInvoice == 'GB') this.getGbRevenueCodeForLinteItem(lineItemId);
  // }

  // getMiRevenueCodeForLinteItem(billingDetailId: string): void {
  //   var revenueCode: string = '';
  //   this.#_dataService.getMiRevenueCodeForLitenItem(billingDetailId).subscribe(
  //     (invoiceBillingLines) => {
  //       revenueCode = invoiceBillingLines;
  //     },
  //     (err: any) => {
  //       revenueCode = 'error';
  //     },
  //     () => {
  //       console.log('revenue code', revenueCode);
  //       this.finishInvoiceDetailsForAdjustment(revenueCode);
  //     },
  //   );
  // }

  // getGbRevenueCodeForLinteItem(linItemId: string): void {
  //   var revenueCode: string = '';
  //   this.#_dataService.getGBRevenueCodeForLitenItem(linItemId).subscribe(
  //     (invoiceBillingLines) => {
  //       revenueCode = invoiceBillingLines;
  //     },
  //     (err: any) => {
  //       revenueCode = 'error';
  //     },
  //     () => {
  //       console.log('revenue code', revenueCode);
  //       this.finishInvoiceDetailsForAdjustment(revenueCode);
  //     },
  //   );
  // }

  //Get adjustment totals for a given invoice billing line
  getTotalsForInvoiceBillingLine(billingLineId: any): IInvoiceAdjustmentAmount {
    var adjustmentTotals: IInvoiceAdjustmentAmount = {} as IInvoiceAdjustmentAmount;
    var subtotal: number = 0;
    var tax: number = 0;
    var grandTotal: number = 0;
    var ii: number = 0;

    try {
      if (this.isGB) {
        for (ii = 0; ii < this.completedAdjustmentsGB.length; ii++) {
          //NOTE: the generatebillinglineitemid could hold the line detail for a GB or MI
          if (this.completedAdjustmentsGB[ii].generateBillingLineItemId == billingLineId) {
            subtotal += this.completedAdjustmentsGB[ii].adjustmentAmount;
            tax += this.completedAdjustmentsGB[ii].taxAmount;
            grandTotal += this.completedAdjustmentsGB[ii].grossAdjustmentValue;
          }
        }
      } else {
        for (ii = 0; ii < this.completedAdjustmentsMI.length; ii++) {
          //NOTE: the generatebillinglineitemid could hold the line detail for a GB or MI
          if (this.completedAdjustmentsMI[ii].billingDetailId == billingLineId) {
            subtotal += this.completedAdjustmentsMI[ii].adjustmentAmount;
            tax += this.completedAdjustmentsMI[ii].taxAmount;
            grandTotal += this.completedAdjustmentsMI[ii].grossAdjustmentValue;
          }
        }
      }

      adjustmentTotals.subtotal = subtotal;
      adjustmentTotals.tax = tax;
      adjustmentTotals.total = grandTotal;
    } catch (err) {}

    return adjustmentTotals;
  }

  onBillingInvoiceLineItemSelected(): void {
    if (this.isGB) {
      this.onBillingInvoiceLineItemSelectedGB();
    } else this.onBillingInvoiceLineItemSelectedMI();
  }

  onBillingInvoiceLineItemSelectedGB(): void {
    let generateBillingLineItemId: string;
    this.disableAdjustmentSubmitButtonFlag = true;

    try {
      // Get the billing line id - could be a MI or GB
      var e = document.getElementById('adjustmentInvoiceLineItem') as HTMLSelectElement;
      var sel = e.selectedIndex;
      var opt = e.options[sel];
      const generateBillingLineItemId: any = (<HTMLOptionElement>opt).value;

      //reset error messages
      this.adjustmentValidationMessages = new AdjustmentValidation();

      ///If this is zero, then make the net/gross data entry read only
      if (generateBillingLineItemId === '0') {
        this.adjustmentDataEntryLocked = 'true';
        (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value = '';
        (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value = '';
        return;
      } else {
        this.adjustmentDataEntryLocked = 'false';
      }

      // Get the adjustments for this billing line item
      let selectedBillingLine: InvoiceAdjustments = this.invoiceBillingLines.find(
        (invoice) => invoice.generateBillingLineItemId == generateBillingLineItemId,
      );

      // Total up all invoce adjustments into  one variable - adjustmentTotals
      const adjustmentTotals: IInvoiceAdjustmentAmount = this.getTotalsForInvoiceBillingLine(
        selectedBillingLine.billingDetailID,
      );

      // Update Billing Line
      const currentIndex = this.invoiceBillingLines.findIndex(
        (invoice) => invoice.generateBillingLineItemId == generateBillingLineItemId,
      );
      this.invoiceBillingLines[currentIndex].calcedAdjustmentAmount = +adjustmentTotals.total;

      let GrossAmountRemaining: number =
        this.invoiceBillingLines[currentIndex].totalAmount +
        this.invoiceBillingLines[currentIndex].calcedAdjustmentAmount;

      //Determine whether this line item is taxable?
      //IF the tax on the current line item is > 0, then consider this item taxable
      this.isLineItemTaxable = this.invoiceBillingLines[currentIndex].tax > 0;

      this.adjustedNetRevenue = GrossAmountRemaining;

      this.maximumAdjustmentGrossAmount = GrossAmountRemaining * -1;

      GrossAmountRemaining = GrossAmountRemaining * -1;
      let grossDisplayAmount: any = this.decimalPipe.transform(GrossAmountRemaining, '1.2-2')?.toString();

      let grossAmountRounded = this.parseFloatIgnoreCommas(grossDisplayAmount) * -1;

      let netDisplayAmount: any;

      // If the amount remaining is 0, that means that all adjustments have removed the positive balance for the invoice billing line.
      // TASK/QUESTION: Should we make this <= instead of ==
      GrossAmountRemaining = +grossAmountRounded;

      if (GrossAmountRemaining === 0) {
        this.disableAdjustmentSubmitButtonFlag = true;
        this._snackBar.open('The balance is $0 and cannot be adjusted.', '', { duration: 3000 });
      }

      // The following sections of code determine calculates the net and gross amounts
      if (this.isLineItemTaxable) {
        //this path has a tax, so calculate tax and subtract from gross amount

        let tempGrossAmount: any = grossAmountRounded * -1;

        let ii = 0;
        let taxRateSummary: number = 0;
        for (ii = 0; ii < this.matchingTaxListForInvoice.length; ii++) {
          taxRateSummary = taxRateSummary + this.matchingTaxListForInvoice[ii].taxPercentage;
        }

        let taxRate = taxRateSummary / 100;
        let netAmount = tempGrossAmount / (1 + taxRate);
        netAmount = netAmount;

        netDisplayAmount = this.decimalPipe.transform(netAmount, '1.2-2')?.toString();
      } else {
        //this path has no tax

        const tempAmount: number = grossAmountRounded * -1;
        netDisplayAmount = this.decimalPipe.transform(tempAmount, '1.2-2')?.toString();
        grossDisplayAmount = this.decimalPipe.transform(tempAmount, '1.2-2')?.toString();
      }
      (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value = netDisplayAmount;
      (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value = grossDisplayAmount;
    } catch (err: any) {
      let errorLogEntry: dbErrorEntry = new dbErrorEntry();

      errorLogEntry.Location = 'Err: invoice-adjustments.component.ts::updateInvoiceDetailsForAdjustment().';
      errorLogEntry.errorMessage = JSON.stringify(err);
      this.apnUtilityService.logErrorToDB(errorLogEntry);
    }
  }

  // Get/Set the subtotal, tax and total for a selected invoice billing line
  // The net amount is the amounts of the billing detail line - previous adjustments for that billing detail line.
  onBillingInvoiceLineItemSelectedMI(): void {
    let generateBillingLineItemId: string;
    this.disableAdjustmentSubmitButtonFlag = true;

    try {
      // Get the billing line id - could be a MI or GB
      var e = document.getElementById('adjustmentInvoiceLineItem') as HTMLSelectElement;
      var sel = e.selectedIndex;
      var opt = e.options[sel];
      const billingDetailId: any = (<HTMLOptionElement>opt).value;

      // const generateBillingLineItemId: string = this.invoiceBillingLines.find(
      //   (invoice) => (invoice.billingDetailID = billingDetailId),
      // ).generateBillingLineItemId;

      //reset error messages
      this.adjustmentValidationMessages = new AdjustmentValidation();

      ///If this is zero, then make the net/gross data entry read only
      if (billingDetailId === '0') {
        this.adjustmentDataEntryLocked = 'true';
        (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value = '';
        (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value = '';
        return;
      } else {
        this.adjustmentDataEntryLocked = 'false';
      }

      // Get the adjustments for this billing line item
      let adjustmentDetails: InvoiceAdjustments = this.getInvoiceAdjustmentDetailsForLineItem(billingDetailId);

      // Total up all invoce adjustments into  one variable - adjustmentTotals
      const adjustmentTotals: IInvoiceAdjustmentAmount = this.getTotalsForInvoiceBillingLine(
        adjustmentDetails.billingDetailID,
      );

      // Update Billing Line
      const currentIndex = this.invoiceBillingLines.findIndex((invoice) => invoice.billingDetailID == billingDetailId);
      this.invoiceBillingLines[currentIndex].calcedAdjustmentAmount = +adjustmentTotals.total;

      // ERROR - this appears to have an invalid value on adjustmentDetails.adjustment.
      // TODO: Get the total invdividual adjustments for this line item, and then aggregate the subtotal, tax and total to subtract from the current line item
      // BUG BUG BUG BUG on invoice number: MI03619022500001
      // let GrossAmountRemaining: number =
      //   (adjustmentDetails.rate + adjustmentDetails.adjustment + adjustmentDetails.tax) * -1;

      let GrossAmountRemaining: number =
        this.invoiceBillingLines[currentIndex].totalAmount +
        this.invoiceBillingLines[currentIndex].calcedAdjustmentAmount;

      //Determine whether this line item is taxable?
      //IF the tax on the current line item is > 0, then consider this item taxable
      this.isLineItemTaxable = this.invoiceBillingLines[currentIndex].tax > 0;

      this.adjustedNetRevenue = GrossAmountRemaining;

      this.maximumAdjustmentGrossAmount = GrossAmountRemaining * -1;

      GrossAmountRemaining = GrossAmountRemaining * -1;
      let grossDisplayAmount: any = this.decimalPipe.transform(GrossAmountRemaining, '1.2-2')?.toString();

      let grossAmountRounded = this.parseFloatIgnoreCommas(grossDisplayAmount) * -1;

      let netDisplayAmount: any;

      // If the amount remaining is 0, that means that all adjustments have removed the positive balance for the invoice billing line.
      // TASK/QUESTION: Should we make this <= instead of ==
      GrossAmountRemaining = +grossAmountRounded;
      if (GrossAmountRemaining === 0) {
        this.disableAdjustmentSubmitButtonFlag = true;
        this._snackBar.open('The balance is $0 and cannot be adjusted.', '', { duration: 3000 });
      }

      // The following sections of code determine calculates the net and gross amounts
      if (this.isLineItemTaxable) {
        //this path has a tax, so calculate tax and subtract from gross amount
        let tempGrossAmount: any = grossAmountRounded * -1;

        let ii = 0;
        let taxRateSummary: number = 0;
        for (ii = 0; ii < this.matchingTaxListForInvoice.length; ii++) {
          taxRateSummary = taxRateSummary + this.matchingTaxListForInvoice[ii].taxPercentage;
        }

        let taxRate = taxRateSummary / 100;
        let netAmount = tempGrossAmount / (1 + taxRate);
        netAmount = netAmount;

        netDisplayAmount = this.decimalPipe.transform(netAmount, '1.2-2')?.toString();
      } else {
        //this path has no tax
        const tempAmount: number = grossAmountRounded * -1;
        netDisplayAmount = this.decimalPipe.transform(tempAmount, '1.2-2')?.toString();
        grossDisplayAmount = this.decimalPipe.transform(tempAmount, '1.2-2')?.toString();
      }

      (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value = netDisplayAmount;
      (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value = grossDisplayAmount;
    } catch (err: any) {
      let errorLogEntry: dbErrorEntry = new dbErrorEntry();

      errorLogEntry.Location = 'Err: invoice-adjustments.component.ts::updateInvoiceDetailsForAdjustment().';
      errorLogEntry.errorMessage = JSON.stringify(err);
      this.apnUtilityService.logErrorToDB(errorLogEntry);
    }
  }

  onValidateAdjustmentData(): void {
    this.disableAdjustmentSubmitButtonFlag = false;
    this.adjustmentValidationMessages = new AdjustmentValidation();

    try {
      if ((<HTMLOptionElement>document.getElementById('adjustmentInvoiceLineItem')).value === '0') {
        this.disableAdjustmentSubmitButtonFlag = true;
      }

      if (this.validateAdjustmentFormNetAdjustmentAmount() === false) {
        this.disableAdjustmentSubmitButtonFlag = true;
      }

      if (this.validateAdjustmentFormGrossAdjustmentAmount() === false) {
        this.disableAdjustmentSubmitButtonFlag = true;
      }

      if (this.validateAdjustmentFormComments() === false) {
        this.disableAdjustmentSubmitButtonFlag = true;
      }

      if (this.validateAdjustmentFormAdjustmentReasons() === false) {
        this.disableAdjustmentSubmitButtonFlag = true;
      }

      let adjustmentAmountString: string = (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value;
      let adjustmentAmountNumeric = this.parseFloatIgnoreCommas(adjustmentAmountString);
      if (adjustmentAmountNumeric === 0) this.disableAdjustmentSubmitButtonFlag = true;
    } catch (err) {
      console.error(err);
    }
  }

  validateAdjustmentFormNetAdjustmentAmount(): boolean {
    let isValid: boolean = true;
    let adjustmentAmountString: string = (<HTMLInputElement>document.getElementById('adjustmentAmountNet')).value;

    this.adjustmentValidationMessages.netAmountMessage = '';

    if (adjustmentAmountString === '') {
      this.adjustmentValidationMessages.netAmountMessage = 'The net adjustment amount cannot be blank';
      isValid = false;
    }

    if (isValid) {
      let adjustmentAmountNumber: number = this.parseFloatIgnoreCommas(adjustmentAmountString);
      if (adjustmentAmountNumber > 0) {
        this.adjustmentValidationMessages.netAmountMessage = 'The net adjustment amount must be less than $0';
        isValid = false;
      }
    }

    if (isValid) {
      adjustmentAmountString = adjustmentAmountString.replace(',', '');
      let adjustmentAmountNumber: number = this.parseFloatIgnoreCommas(adjustmentAmountString);
      let roundedMaximumAdjustmentNetAmount: any;

      if (this.maximumAdjustmentNetAmount !== null) roundedMaximumAdjustmentNetAmount = adjustmentAmountNumber; //this.decimalPipe.transform(this.maximumAdjustmentNetAmount,"1.2-2")

      if (adjustmentAmountNumber < roundedMaximumAdjustmentNetAmount) {
        this.adjustmentValidationMessages.netAmountMessage =
          'The net adjustment amount cannot be greater than $' +
          this.decimalPipe.transform(this.maximumAdjustmentNetAmount, '1.2-2')?.toString();
        isValid = false;
      }
    }

    return isValid;
  }

  validateAdjustmentFormGrossAdjustmentAmount(): boolean {
    let isValid: boolean = true;
    let adjustmentAmountString: string = (<HTMLInputElement>document.getElementById('adjustmentAmountGross')).value;

    this.adjustmentValidationMessages.grossAmountMessage = '';

    if (adjustmentAmountString === '') {
      this.adjustmentValidationMessages.grossAmountMessage = 'The gross adjustment amount cannot be blank';
      isValid = false;
    }

    if (isValid) {
      let adjustmentAmountNumber: number = this.parseFloatIgnoreCommas(adjustmentAmountString);
      if (adjustmentAmountNumber > 0) {
        this.adjustmentValidationMessages.grossAmountMessage = 'The gross adjustment amount must be less than $0';
        isValid = false;
      }
    }

    if (isValid) {
      let maximumAdjustmentGrossAmountString: any = this.decimalPipe
        .transform(this.maximumAdjustmentGrossAmount, '1.2-2')
        ?.toString();
      let maximumAdjustmentGrossAmountRounded = this.parseFloatIgnoreCommas(maximumAdjustmentGrossAmountString);
      let adjustmentAmountNumber: number = this.parseFloatIgnoreCommas(adjustmentAmountString);
      if (adjustmentAmountNumber < maximumAdjustmentGrossAmountRounded) {
        this.adjustmentValidationMessages.grossAmountMessage =
          'The gross adjustment amount cannot be greater than $' +
          this.decimalPipe.transform(this.maximumAdjustmentGrossAmount, '1.2-2')?.toString();
        isValid = false;
      }
    }

    return isValid;
  }

  validateAdjustmentFormComments(): boolean {
    let isValid: boolean = true;

    this.adjustmentValidationMessages.commentMessage = '';

    if ((<HTMLInputElement>document.getElementById('adjustmentComment')).value === '') {
      this.adjustmentValidationMessages.commentMessage = 'Enter a comment before submitting adjustment';
      isValid = false;
    }

    return isValid;
  }

  validateAdjustmentFormAdjustmentReasons(): boolean {
    let isValid: boolean = true;

    this.adjustmentValidationMessages.adjustmentReasonMessage = '';

    if ((<HTMLOptionElement>document.getElementById('adjustmentReasons')).value === '0') {
      this.adjustmentValidationMessages.adjustmentReasonMessage = 'Select a Reason before submitting adjustment';
      isValid = false;
    }

    return isValid;
  }

  getInvoiceAdjustmentDetailsForLineItem(primaryKeyValue: string): InvoiceAdjustments {
    let ii: number = 0;
    let adjustmentDetails: InvoiceAdjustments = new InvoiceAdjustments();
    for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
      if (primaryKeyValue === this.invoiceBillingLines[ii].primaryKey) {
        adjustmentDetails = this.invoiceBillingLines[ii];
      }
    }
    return adjustmentDetails;
  }

  getCurrentDate(): any {
    var today = new Date();
    var returnDate = '';

    var dd = today.getDate();
    var mm = today.getMonth() + 1;
    var yyyy = today.getFullYear();

    returnDate += `${mm}/${dd}/${yyyy}`;

    return returnDate;
  }

  parseFloatIgnoreCommas(theNumber: string): number {
    var numberNoCommas = theNumber.replace(/,/g, '');
    return parseFloat(numberNoCommas);
  }
}
